/**
 * Command Center map helpers for War Effort.
 * Distinguishes omitted broadcast fields from an authoritative empty/updated cell list.
 */

export function mergeCommandCenterBoostCellIds(incoming, previous) {
  if (!Array.isArray(incoming)) {
    if (previous == null) return [];
    if (!Array.isArray(previous)) {
      throw new Error('mergeCommandCenterBoostCellIds: previous must be an array when provided');
    }
    return previous.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
  }
  return incoming.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
}

export function mergeCommandCenterBoostFields(incoming, previous) {
  if (incoming == null || typeof incoming !== 'object') {
    throw new Error('mergeCommandCenterBoostFields: incoming is required');
  }
  return {
    viewer_command_center_boost_cell_ids: mergeCommandCenterBoostCellIds(
      incoming.viewer_command_center_boost_cell_ids,
      previous?.viewer_command_center_boost_cell_ids
    ),
    opponent_command_center_boost_cell_ids: mergeCommandCenterBoostCellIds(
      incoming.opponent_command_center_boost_cell_ids,
      previous?.opponent_command_center_boost_cell_ids
    )
  };
}

/**
 * Drop Command Center stripe cells that are no longer owned by the expected side.
 * HQ cells can be missing from war_effort_status, so alwaysIncludeCellIds keeps those.
 * When ownership has not loaded yet (empty map), cell IDs are left unchanged.
 */
export function filterCommandCenterCellsByOwner(cellIds, cellOwnersMap, ownerFactionSet, alwaysIncludeCellIds) {
  if (!Array.isArray(cellIds)) {
    throw new Error('filterCommandCenterCellsByOwner: cellIds must be an array');
  }
  if (cellOwnersMap == null || typeof cellOwnersMap !== 'object') {
    throw new Error('filterCommandCenterCellsByOwner: cellOwnersMap is required');
  }
  if (!(ownerFactionSet instanceof Set)) {
    throw new Error('filterCommandCenterCellsByOwner: ownerFactionSet must be a Set');
  }
  const normalized = cellIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
  if (Object.keys(cellOwnersMap).length === 0) {
    return normalized;
  }
  const alwaysInclude = Array.isArray(alwaysIncludeCellIds)
    ? new Set(alwaysIncludeCellIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id)))
    : new Set();
  return normalized.filter((id) => {
    if (alwaysInclude.has(id)) return true;
    const owner = cellOwnersMap[String(id)] || cellOwnersMap[id];
    return owner != null && ownerFactionSet.has(owner);
  });
}
